// Settle one business for the in-flight week — runs the formulas, emits txns.
// Called for every BusinessInstance at SettleWeek. Active weeklyEffects can adjust
// demand or cogs (Phase 2 events) — for MVP we apply effects if present, no-op otherwise.

import type { BusinessDef } from '../config/types.js';
import type {
  BusinessInstance,
  EffectInstance,
  GameState,
} from '../state/types.js';
import { businessWeeklyResult, repFactor, wellbeingFactor } from './formulas.js';
import { applyTxn } from '../ledger/append.js';

export type OperateInputs = {
  def: BusinessDef;
  reputation: number;
  effects: readonly EffectInstance[];
};

function effectiveDemand(def: BusinessDef, effects: readonly EffectInstance[]): number {
  let base = def.baseDemand;
  for (const e of effects) {
    for (const eff of e.effects) {
      if (eff.kind !== 'demandMultiplier') continue;
      if (eff.businessDefId && eff.businessDefId !== def.id) continue;
      base = base * eff.multiplier;
    }
  }
  return base;
}

function effectiveCogs(def: BusinessDef, effects: readonly EffectInstance[]): number {
  let cogs = def.cogsPerUnit;
  for (const e of effects) {
    for (const eff of e.effects) {
      if (eff.kind !== 'cogsDelta') continue;
      if (eff.businessDefId && eff.businessDefId !== def.id) continue;
      cogs = cogs + eff.delta;
    }
  }
  return Math.max(0, cogs);
}

export function operateBusinessForWeek(
  state: GameState,
  instance: BusinessInstance,
  inputs: OperateInputs,
): GameState {
  const { def, reputation, effects } = inputs;
  // Owner wellbeing scales demand: burnout costs sales, balance earns a little.
  const wb = wellbeingFactor(state.profile.happiness);
  const result = businessWeeklyResult({
    baseDemand: effectiveDemand(def, effects) * wb,
    price: instance.price,
    sweetPrice: def.sweetPrice,
    marketingSpend: instance.marketingSpend,
    qualityFactor: def.qualityFactorDefault,
    repFactor: repFactor(reputation),
    capacity: def.capacity,
    cogsPerUnit: effectiveCogs(def, effects),
    fixedPerWeek: def.fixedPerWeek,
  });

  let s = state;
  if (result.revenue > 0) {
    s = applyTxn(s, {
      type: 'revenue',
      amount: result.revenue,
      category: 'active',
      note: `${def.name} — ${result.unitsSold} units @ B$${instance.price}`,
    });
  }
  if (result.cogs > 0) {
    s = applyTxn(s, {
      type: 'cogs',
      amount: -result.cogs,
      category: 'expense',
      note: `${def.name} — COGS`,
    });
  }
  if (result.fixed > 0) {
    s = applyTxn(s, {
      type: 'fixed',
      amount: -result.fixed,
      category: 'expense',
      note: `${def.name} — fixed costs`,
    });
  }
  if (result.marketing > 0) {
    s = applyTxn(s, {
      type: 'marketing',
      amount: -result.marketing,
      category: 'expense',
      note: `${def.name} — marketing`,
    });
  }
  if (result.wages > 0) {
    s = applyTxn(s, {
      type: 'wages',
      amount: -result.wages,
      category: 'expense',
      note: `${def.name} — wages`,
    });
  }

  // Bump weeksOperated on this business instance.
  return {
    ...s,
    businesses: s.businesses.map((b) =>
      b.id === instance.id ? { ...b, weeksOperated: b.weeksOperated + 1 } : b,
    ),
  };
}
